import React, { useRef, useEffect } from "react";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/flatpickr.min.css";
import type { Instance } from "flatpickr/dist/types/instance";
import { Calendar } from "lucide-react";
import { datePickerStyles } from "../../styles/components/common/DatePickerStyles";

interface DatePickerProps {
  value: string;
  onChange: (selectedDates: Date[]) => void;
  label?: string;
  error?: string;
  required?: boolean;
  className?: string;
}

export const DatePicker: React.FC<DatePickerProps> = ({
  value,
  onChange,
  label = "Select a date",
  error,
  required,
  className = "",
}) => {
  const flatpickrRef = useRef<Instance | null>(null);

  useEffect(() => {
    if (flatpickrRef.current && value) {
      const [day, month, year] = value.split("-");
      const parsedDate = new Date(`${year}-${month}-${day}`);
      flatpickrRef.current.setDate(parsedDate, false);
    }
  }, [value]);

  useEffect(() => {
    const fp = flatpickrRef.current;
    if (!fp) return;

    const injectButtons = () => {
      const calendarContainer = fp.calendarContainer;
      if (!calendarContainer) return;

      const existingFooter = calendarContainer.querySelector(".flatpickr-custom-footer");
      if (existingFooter) existingFooter.remove();

      const footer = document.createElement("div");
      footer.className =
        "flatpickr-custom-footer px-4 py-2 flex justify-between border-t text-sm text-blue-600";

      const clearBtn = document.createElement("span");
      clearBtn.textContent = "Clear";
      clearBtn.className = "cursor-pointer hover:underline";
      clearBtn.onclick = () => {
        fp.clear();
        onChange([]);
      };

const todayBtn = document.createElement("span");
todayBtn.textContent = "Today";
todayBtn.className = "cursor-pointer hover:underline";
todayBtn.onclick = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  fp.setDate(today, true);     // set in Flatpickr
  onChange([today]);           // notify React form
};


      footer.appendChild(clearBtn);
      footer.appendChild(todayBtn);
      calendarContainer.appendChild(footer);
    };

    // Inject buttons and set minDate only on calendar open
    fp.config.onOpen.push(() => {
      fp.set("minDate", new Date()); // prevent selecting past dates from UI
      setTimeout(injectButtons, 0);
    });

    fp.config.onMonthChange?.push(() => setTimeout(injectButtons, 0));
  }, [onChange]);

  const toggleCalendar = () => {
    const fp = flatpickrRef.current;
    if (fp) {
      fp.isOpen ? fp.close() : fp.open();
    }
  };

  return (
    <div className="mb-4">
      {label && (
        <label className={datePickerStyles.label}>
          {label}
          {required && <span className={datePickerStyles.required}>*</span>}
        </label>
      )}

      <div
        className={`${datePickerStyles.flatpickrWrapper} relative cursor-pointer`}
        onClick={toggleCalendar}
      >
        <Flatpickr
          value={value}
          onChange={onChange}
          options={{
            dateFormat: "d-m-Y",
            allowInput: true,
            clickOpens: false,
            onReady: (_selectedDates, _dateStr, instance) => {
              flatpickrRef.current = instance;
            },
          }}
          className={`
            ${datePickerStyles.flatpickrInput} 
            ${error ? datePickerStyles.inputError : ""} 
            pr-10 ${className}
          `}
          placeholder="Select a date"
          readOnly
        />

        <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
          <Calendar className="w-4 h-4 text-gray-500 dark:text-white/70" />
        </div>
      </div>

      {error && <p className={datePickerStyles.errorText}>{error}</p>}
    </div>
  );
};
